import React, { useEffect, useCallback, useRef, useState } from 'react';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanToolbar from './FloorPlanToolbar';
import FloorPlanPropertyPanel from './FloorPlanPropertyPanel';
import FloorPlanAssetLibrary from './FloorPlanAssetLibrary';
import useFloorPlanState from './useFloorPlanState';
import useFloorPlanTools, { TOOLS } from './useFloorPlanTools';
import { formatArea, findRooms, polygonArea, snapToGrid, snapToEndpoint } from './floorPlanUtils';

function FloorPlanTab({ floorPlanData, onFloorPlanUpdate }) {
  const stageRef = useRef(null);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);

  const {
    state,
    selectedId,
    setSelectedId,
    getSelectedElement,
    deleteSelected,
    addWall,
    updateWall,
    addOpening,
    updateOpening,
    addFurniture,
    updateFurniture,
    updateRoom,
    updateSettings,
    updateViewport,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useFloorPlanState(floorPlanData, onFloorPlanUpdate);

  const {
    activeTool,
    setActiveTool,
    drawingWall,
    previewPoint,
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    handleCanvasMouseUp,
    handleCanvasDoubleClick,
    handleWheel,
    handleKeyDown,
    handleKeyUp,
    cancelDrawing,
    zoomIn,
    zoomOut,
  } = useFloorPlanTools({
    state,
    addWall,
    addOpening,
    setSelectedId,
    updateViewport,
  });

  // Open asset library when furniture tool is selected
  useEffect(() => {
    if (activeTool === TOOLS.FURNITURE) {
      setShowAssetLibrary(true);
    }
  }, [activeTool]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      handleKeyDown(e);

      // Undo/Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      // Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if focused on an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        deleteSelected();
      }
    };

    const handleGlobalKeyUp = (e) => {
      handleKeyUp(e);
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('keyup', handleGlobalKeyUp);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('keyup', handleGlobalKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, undo, redo, deleteSelected]);

  const handleExport = useCallback(() => {
    if (!stageRef.current) return;
    const dataURL = stageRef.current.toDataURL({ pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = 'floor-plan.png';
    link.href = dataURL;
    link.click();
  }, []);

  const handleAssetSelect = useCallback((asset) => {
    // Place furniture at center of viewport
    const stage = stageRef.current;
    const stageW = stage ? stage.width() : 800;
    const stageH = stage ? stage.height() : 600;
    const centerX = (stageW / 2 - state.viewport.x) / state.viewport.zoom;
    const centerY = (stageH / 2 - state.viewport.y) / state.viewport.zoom;
    addFurniture(asset.id, centerX, centerY, asset);
    setActiveTool(TOOLS.SELECT);
  }, [state.viewport, addFurniture, setActiveTool]);

  const handlePan = useCallback((dx, dy) => {
    const step = 50;
    updateViewport({
      x: state.viewport.x + dx * step,
      y: state.viewport.y + dy * step,
    });
  }, [updateViewport, state.viewport]);

  const snapPoint = useCallback((point) => {
    let snapped = point;
    if (state.settings.snapToGrid) {
      snapped = snapToGrid(snapped, state.settings.gridSize);
    }
    snapped = snapToEndpoint(snapped, state.walls, state.settings.gridSize * 0.5);
    return snapped;
  }, [state.settings.snapToGrid, state.settings.gridSize, state.walls]);

  const handleWallDragEnd = useCallback((wallId, updates) => {
    if (updates.x1 !== undefined && updates.y1 !== undefined) {
      const snapped = snapPoint({ x: updates.x1, y: updates.y1 });
      updates = { ...updates, x1: snapped.x, y1: snapped.y };
    }
    if (updates.x2 !== undefined && updates.y2 !== undefined) {
      const snapped = snapPoint({ x: updates.x2, y: updates.y2 });
      updates = { ...updates, x2: snapped.x, y2: snapped.y };
    }
    updateWall(wallId, updates);
  }, [updateWall, snapPoint]);

  const handleWallDrag = useCallback((wallId, updates) => {
    if (state.settings.snapToGrid && updates.x1 !== undefined) {
      const snapped = snapToGrid({ x: updates.x1, y: updates.y1 }, state.settings.gridSize);
      const dx = snapped.x - updates.x1;
      const dy = snapped.y - updates.y1;
      updates = {
        x1: updates.x1 + dx,
        y1: updates.y1 + dy,
        x2: updates.x2 + dx,
        y2: updates.y2 + dy,
      };
    }
    updateWall(wallId, updates);
  }, [updateWall, state.settings.snapToGrid, state.settings.gridSize]);

  const handleFurnitureDragEnd = useCallback((furnId, updates) => {
    if (state.settings.snapToGrid) {
      const snapped = snapToGrid({ x: updates.x, y: updates.y }, state.settings.gridSize);
      updates = { ...updates, x: snapped.x, y: snapped.y };
    }
    updateFurniture(furnId, updates);
  }, [updateFurniture, state.settings.snapToGrid, state.settings.gridSize]);

  const selection = getSelectedElement();

  // Calculate total area from detected rooms
  const detectedRooms = findRooms(state.walls);
  const totalArea = detectedRooms.reduce((sum, r) => sum + polygonArea(r.vertices), 0);

  return (
    <div className="floor-plan-tab d-flex flex-column h-100">
      <FloorPlanToolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        settings={state.settings}
        onSettingsChange={updateSettings}
        onExport={handleExport}
        drawingWall={drawingWall}
        onCancelDrawing={cancelDrawing}
      />

      <div className="d-flex flex-grow-1 overflow-hidden">
        <FloorPlanCanvas
          ref={stageRef}
          state={state}
          selectedId={selectedId}
          drawingWall={drawingWall}
          previewPoint={previewPoint}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onDoubleClick={handleCanvasDoubleClick}
          onWheel={handleWheel}
          onSelectElement={setSelectedId}
          onWallDragEnd={handleWallDragEnd}
          onWallDrag={handleWallDrag}
          onFurnitureDragEnd={handleFurnitureDragEnd}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onPan={handlePan}
          activeTool={activeTool}
        />

        {selection && (
          <FloorPlanPropertyPanel
            selection={selection}
            state={state}
            onUpdateWall={updateWall}
            onUpdateOpening={updateOpening}
            onUpdateFurniture={updateFurniture}
            onUpdateRoom={updateRoom}
            onDelete={deleteSelected}
            unit={state.settings.unit}
          />
        )}
      </div>

      {/* Status bar */}
      <div className="floor-plan-status-bar d-flex align-items-center gap-3 px-3 py-1 border-top bg-white">
        <small className="text-muted">
          <i className="bi bi-border-outer me-1"></i>
          Walls: {state.walls.length}
        </small>
        <small className="text-muted">
          <i className="bi bi-door-open me-1"></i>
          Openings: {state.openings.length}
        </small>
        <small className="text-muted">
          <i className="bi bi-lamp me-1"></i>
          Furniture: {state.furniture.length}
        </small>
        {totalArea > 0 && (
          <small className="text-muted">
            <i className="bi bi-bounding-box me-1"></i>
            Area: {formatArea(totalArea, state.settings.unit)}
          </small>
        )}
        <div className="flex-grow-1" />
        <small className="text-muted">
          Zoom: {Math.round(state.viewport.zoom * 100)}%
        </small>
      </div>

      <FloorPlanAssetLibrary
        show={showAssetLibrary}
        onHide={() => {
          setShowAssetLibrary(false);
          if (activeTool === TOOLS.FURNITURE) {
            setActiveTool(TOOLS.SELECT);
          }
        }}
        onSelectAsset={handleAssetSelect}
      />
    </div>
  );
}

export default FloorPlanTab;
